import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Clock, AlertTriangle, XCircle, Loader2, FileText } from 'lucide-react';
import { DOC_TYPE_LABELS } from '@/lib/verification/docTypeLabels';
import type { VerificationDocument } from '@/hooks/useCompanyVerification';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  documents: VerificationDocument[];
}

const STATUS_BADGE: Record<string, { label: string; cls: string; icon: any }> = {
  pending_parse: { label: 'Detecting…', cls: 'bg-muted text-muted-foreground border-border', icon: Loader2 },
  in_review: { label: 'In Review', cls: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  auto_approved: { label: 'Approved', cls: 'bg-success-subtle text-success border-success/30', icon: ShieldCheck },
  approved: { label: 'Approved', cls: 'bg-success-subtle text-success border-success/30', icon: ShieldCheck },
  rejected: { label: 'Rejected', cls: 'bg-danger/10 text-danger border-danger/30', icon: XCircle },
  expired: { label: 'Expired', cls: 'bg-orange-100 text-orange-700 border-orange-300', icon: AlertTriangle },
  parse_failed: { label: 'Needs admin', cls: 'bg-amber-100 text-amber-700 border-amber-300', icon: AlertTriangle },
};

export function UploadedDocsList({ documents }: Props) {
  if (documents.length === 0) {
    return (
      <Card className="p-6 text-center text-xs text-muted-foreground">
        No documents uploaded yet.
      </Card>
    );
  }

  const openFile = async (path: string) => {
    const { data } = await supabase.storage.from('compliance-documents').createSignedUrl(path, 60 * 10);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
  };

  return (
    <Card className="p-4">
      <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
        Your uploaded documents
      </h3>
      <div className="divide-y divide-border">
        {documents.map((doc) => {
          const badge = STATUS_BADGE[doc.review_status] || STATUS_BADGE.pending_parse;
          const Icon = badge.icon;
          const label = DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type;
          return (
            <div key={doc.id} className="flex items-center justify-between gap-3 py-2.5">
              <div className="min-w-0 flex-1 flex items-center gap-2.5">
                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <button
                    onClick={() => openFile(doc.storage_path)}
                    className="text-sm font-medium text-foreground hover:underline truncate block max-w-full text-left"
                    title={doc.file_name}
                  >
                    {doc.file_name}
                  </button>
                  <div className="text-[11px] text-muted-foreground">
                    {label}
                    {doc.document_expiry_date && ` · Expires ${new Date(doc.document_expiry_date).toLocaleDateString()}`}
                  </div>
                  {doc.reviewer_notes && doc.review_status === 'rejected' && (
                    <p className="text-[11px] text-danger mt-0.5">Reviewer: {doc.reviewer_notes}</p>
                  )}
                </div>
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap ${badge.cls}`}>
                <Icon className={`h-3 w-3 ${doc.review_status === 'pending_parse' ? 'animate-spin' : ''}`} />
                {badge.label}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
